import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Zap, BarChart3, Shield, CheckCircle, Users } from 'lucide-react';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

const Technicians = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <NavBar />

      {/* Hero Section */}
      <section className="relative text-white pt-20 pb-20 min-h-[600px]" style={{
        backgroundImage: 'url(/Technicians.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-800/70 to-transparent"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl text-left">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
              <Settings className="w-5 h-5" />
              <span className="text-sm font-medium">Professional Tools</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              For EEG Technicians
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              Advanced tools and resources for neurofeedback technicians delivering exceptional client care
            </p>

            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00897B] hover:bg-[#00796B] text-white rounded-full text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              Get Started Today
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Why AIMS Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why AIMS for Technicians
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Everything you need to deliver professional neurofeedback services
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <Zap className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Easy to Use
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Intuitive interface designed for technicians. Set up sessions quickly and focus on what matters - your clients.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <BarChart3 className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Real-Time Monitoring
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Track client progress in real-time with detailed analytics and comprehensive reporting tools.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-3xl p-8 hover:border-[#00897B] hover:shadow-2xl transition-all duration-300">
                <div className="w-14 h-14 bg-[#00897B]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00897B] transition-colors">
                  <Shield className="w-7 h-7 text-[#00897B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Professional Support
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Access to training resources, technical support, and a community of neurofeedback professionals.
                </p>
              </div>
            </div>
          </div>

          {/* Key Capabilities Section */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="order-2 md:order-1">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8">
                  Professional Capabilities
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Session Management</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Easily schedule, track, and document client sessions with our streamlined interface.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Protocol Customization</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Customize neurofeedback protocols based on individual client needs and assessment results.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Progress Tracking</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Monitor client improvements with detailed metrics and visual progress reports.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#00897B] rounded-lg flex items-center justify-center mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure Data Management</h3>
                      <p className="text-gray-700 leading-relaxed">
                        HIPAA-compliant data storage ensures client confidentiality and professional standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Image/Visual */}
              <div className="flex justify-center order-1 md:order-2">
                <div className="relative bg-gradient-to-br from-[#00897B]/10 to-gray-100 rounded-3xl p-12">
                  <div className="w-full max-w-md">
                    <Settings className="w-full h-auto text-[#00897B] opacity-20" size={300} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Training & Resources Section */}
          <div className="mb-20">
            <div className="bg-gradient-to-br from-[#C8E6E1] to-[#00897B]/20 rounded-3xl p-8 sm:p-10 md:p-12 shadow-xl">
              <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Training & Resources
                </h2>
                <p className="text-xl text-gray-700">
                  Comprehensive support to help you succeed
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-[#00897B] rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Expert Training
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Access comprehensive training materials, video tutorials, and certification programs to enhance your skills.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-[#00897B] rounded-xl flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Clinical Resources
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Evidence-based protocols, research papers, and best practice guidelines at your fingertips.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-[#00897B] rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    24/7 Support
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Round-the-clock technical support to ensure smooth operations and client satisfaction.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-[#00897B] rounded-xl flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Community Access
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Connect with fellow technicians, share experiences, and learn from the best in the field.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-[#00897B] rounded-3xl p-12 sm:p-16 shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#00897B]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Get Started?
              </h2>

              <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">
                Join the network of professional neurofeedback technicians using AIMS
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/register')}
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00897B] hover:bg-[#00796B] text-white rounded-full text-lg font-medium transition-all hover:scale-105 shadow-lg"
                >
                  Sign Up Now
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => navigate('/lbw')}
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 rounded-full text-lg font-medium transition-all hover:scale-105 shadow-lg"
                >
                  Learn More
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Technicians;
